import { Avatar, Box, Tooltip, useMediaQuery, Theme } from '@mui/material';
import { IconButton, Dialog } from '@sm-dashboard/ui-components';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import DeleteIcon from '@mui/icons-material/Delete';
import { getUser } from '@/util/getUser';
import { useNavigate } from 'react-router-dom';
import { CONTACT_US_NUMBER } from '@/constants';
import { ISRAEL_REGULATIONS_ROUTE, WORLD_REGULATIONS_ROUTE } from '@/util/regulationsRoute';
import { useAppContext } from '@/context/context';
import { useRef, useState } from 'react';
import { useFetch } from '@/hooks/useFetch';
import ReactGA from 'react-ga4';
import { updateShluchimUrl, getShluchimByIdUrl } from '@/constants';
import avatar from '@/assets/avatar.png';
import avatarW from '@/assets/avatar-w.png';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import { Divider } from '@mui/material';

export const ProfileSection = ({
  onNavigate,
  drawerWidth,
  logoSrc,
  onLogoClick
}: {
  onNavigate?: () => void;
  drawerWidth?: number;
  logoSrc?: string;
  onLogoClick?: () => void;
} = {}) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const user = getUser();
  const navigate = useNavigate();
  const { setUser, previewProfile, setPreviewProfile, shaliach, setSnackbar, userPermission } = useAppContext() || {
    setUser: () => undefined,
    previewProfile: null,
    setPreviewProfile: () => undefined,
    shaliach: undefined,
    setSnackbar: () => undefined,
    userPermission: undefined
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { fetchData: fetchUpdateData } = useFetch();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const handleLogout = () => {
    window.localStorage.removeItem('token');
    setUser(undefined);
    navigate('/login');
    onNavigate?.();
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const currentUser = shaliach || user;
      if (!currentUser?.id) return;

      ReactGA.event({
        category: 'User',
        action: 'העלאת תמונת פרופיל',
        label: 'העלאת תמונת פרופיל'
      });

      try {
        const formData = new FormData();
        formData.append('profileImage', file);

        await fetchUpdateData({
          method: 'POST',
          url: `${updateShluchimUrl}?id=${currentUser.id}`,
          data: formData
        });
        const updatedShaliach = await fetchUpdateData({
          method: 'GET',
          url: `${getShluchimByIdUrl}?id=${currentUser.id}`
        });
        if (updatedShaliach?.profileImage) {
          setPreviewProfile?.(updatedShaliach.profileImage);
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        window.location.reload();
      } catch (error) {
        console.error('Error uploading profile image:', error);
        setSnackbar?.({
          severity: 'error',
          message: 'שגיאה בהעלאת תמונת הפרופיל',
          open: true
        });
      }
    }
  };

  const handleDeletePhoto = async () => {
    const currentUser = shaliach || user;
    if (!currentUser?.id) return;

    ReactGA.event({
      category: 'User',
      action: 'מחיקת תמונת פרופיל',
      label: 'מחיקת תמונת פרופיל'
    });

    try {
      await fetchUpdateData({
        method: 'POST',
        url: `${updateShluchimUrl}?id=${currentUser.id}`,
        data: {
          profileImage: null
        }
      });

      setPreviewProfile?.(null);

      window.location.reload();
    } catch (error) {
      console.error('Error deleting profile image:', error);
      setSnackbar?.({
        severity: 'error',
        message: 'שגיאה במחיקת תמונת הפרופיל',
        open: true
      });
    }
  };

  const handleImageClick = () => {
    if (hasCustomImage) {
      setShowImageDialog(true);
    }
  };

  const currentUser = shaliach || user;
  const hasCustomImage =
    previewProfile ||
    (currentUser?.profileImage && currentUser.profileImage !== avatar && currentUser.profileImage !== avatarW);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        ...(isSmallScreen && {
          width: '50%',
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '16px',
          backgroundColor: '#ffffff80',
          borderRadius: '25px'
        })
      }}
    >
      <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileChange} />

      <Box
        sx={{
          position: 'relative',
          '&:hover .profile-actions': {
            opacity: 1,
            visibility: 'visible'
          }
        }}
      >
        <Tooltip title={hasCustomImage ? 'לחץ להצגת תמונה בגדול' : ''}>
          <Avatar
            alt={currentUser?.nickName || currentUser?.firstName || currentUser?.lastName}
            src={
              previewProfile || currentUser?.profileImage || (currentUser?.tags?.includes('שליחה') ? avatarW : avatar)
            }
            sx={{
              width: 60,
              height: 60,
              border: '2px solid rgba(0, 0, 0, 0.1)',
              cursor: hasCustomImage ? 'pointer' : 'default',
              transition: 'transform 0.2s ease',
              '&:hover': hasCustomImage
                ? {
                    transform: 'scale(1.05)'
                  }
                : {}
            }}
            onClick={handleImageClick}
          >
            {((currentUser?.firstName || '').charAt(0) + (currentUser?.lastName || '').charAt(0)).toUpperCase() ||
              (currentUser?.nickName || '').charAt(0).toUpperCase() ||
              'מל'}
          </Avatar>
        </Tooltip>

        <Box
          className="profile-actions"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease, visibility 0.2s ease'
          }}
        >
          <Tooltip title="העלאת תמונת פרופיל">
            <IconButton
              shape="circle"
              color="primary"
              size="small"
              onClick={handlePhotoClick}
              aria-label="העלאת תמונת פרופיל"
            >
              <AddAPhotoIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {hasCustomImage && (
          <Box
            className="profile-actions"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              opacity: 0,
              visibility: 'hidden',
              transition: 'opacity 0.2s ease, visibility 0.2s ease'
            }}
          >
            <Tooltip title="מחיקת תמונת פרופיל">
              <IconButton
                shape="circle"
                color="error"
                size="small"
                onClick={() => setShowDeleteDialog(true)}
                aria-label="מחיקת תמונת פרופיל"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <ShaliachAndBranchName variant={logoSrc ? 'sidebar' : 'default'} />

      {!logoSrc && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}
          >
            <Tooltip title="הפרופיל שלי">
              <IconButton
                shape="circle"
                variant="text"
                color="secondary"
                onClick={() => {
                  navigate('/system/shluchim/profile');
                  onNavigate?.();
                }}
                aria-label="הפרופיל שלי"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="יציאה מהמערכת">
              <IconButton
                shape="circle"
                variant="text"
                color="secondary"
                onClick={handleLogoutClick}
                aria-label="יציאה מהמערכת"
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ width: (drawerWidth ?? 95) - 20, my: 1 }} />

          <Box sx={{ textAlign: 'center' }}>
            <Box
              onClick={() => {
                const whatsappUrl = `https://wa.me/${CONTACT_US_NUMBER}`;
                window.open(whatsappUrl, '_blank');
                onNavigate?.();
              }}
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: 'rgba(0, 0, 0, 0.6)',
                display: 'block',
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              צור קשר
            </Box>
            {[
              { route: ISRAEL_REGULATIONS_ROUTE, label: 'תקנונים ומדריכים — ישראל' },
              { route: WORLD_REGULATIONS_ROUTE, label: 'תקנונים ומדריכים — חו״ל' }
            ]
              .filter(item => userPermission?.hasPagePermission(item.route))
              .map(item => (
                <Box
                  key={item.route}
                  onClick={() => {
                    navigate(item.route);
                    onNavigate?.();
                  }}
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'rgba(0, 0, 0, 0.6)',
                    display: 'block',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {item.label}
                </Box>
              ))}
          </Box>
        </>
      )}

      {logoSrc && (
        <Box
          sx={{
            width: 70,
            height: 45,
            mt: 1,
            alignContent: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: onLogoClick ? 'pointer' : 'default'
          }}
          onClick={onLogoClick}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Logo"
            sx={{
              width: 70,
              height: 45,
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Box>
      )}

      <DynamicDialog
        open={showDeleteDialog}
        title="מחיקת תמונת פרופיל"
        subtitle="האם אתה בטוח שברצונך למחוק את תמונת הפרופיל?"
        handleOkClose={() => {
          handleDeletePhoto();
          setShowDeleteDialog(false);
        }}
        handleNotOkClose={() => setShowDeleteDialog(false)}
      />

      <DynamicDialog
        open={showLogoutDialog}
        title="יציאה מהמערכת"
        subtitle="האם אתה בטוח שברצונך לצאת מהמערכת?"
        handleOkClose={() => {
          handleLogout();
          setShowLogoutDialog(false);
        }}
        handleNotOkClose={() => setShowLogoutDialog(false)}
      />

      <Dialog
        open={showImageDialog}
        onClose={() => setShowImageDialog(false)}
        maxWidth="md"
        fullWidth
        contentPadding="none"
      >
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={previewProfile || currentUser?.profileImage}
            alt="תמונת פרופיל"
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export const ShaliachAndBranchName = ({
  onClick,
  alignTo = 'center',
  variant = 'default'
}: {
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  alignTo?: 'end' | 'center';
  variant?: 'default' | 'sidebar';
}) => {
  const user = getUser();
  const { shaliach } = useAppContext() || { shaliach: undefined };

  const currentUser = shaliach || user;

  const getFullName = () => {
    if (currentUser?.nickName) {
      return currentUser.nickName;
    }

    const firstName = currentUser?.firstName || '';
    const lastName = currentUser?.lastName || '';

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }

    return 'משתמש';
  };

  const fullName = getFullName();
  const branchName = currentUser?.Branch?.branchName || 'ללא סניף';

  const branchNameSx =
    variant === 'sidebar'
      ? {
          fontFamily: 'Noto Sans Hebrew',
          fontWeight: 500,
          fontSize: 10,
          textAlign: 'center' as const,
          color: '#6B1224',
          display: 'block',
          maxWidth: '21ch',
          wordBreak: 'break-word' as const,
          overflowWrap: 'break-word' as const,
          whiteSpace: 'normal' as const
        }
      : {
          fontSize: 12,
          lineHeight: '150%',
          color: 'rgba(0, 0, 0, 0.6)',
          textAlign: alignTo,
          display: 'block',
          fontWeight: 500,
          mb: 0.5,
          maxWidth: '21ch',
          wordBreak: 'break-word' as const,
          overflowWrap: 'break-word' as const,
          whiteSpace: 'normal' as const
        };

  return (
    <Box onClick={onClick} sx={{ textAlign: alignTo, mb: 1, maxWidth: '21ch', width: 'fit-content' }}>
      <Box
        sx={{
          fontSize: 12,
          ...(variant !== 'sidebar' ? { lineHeight: '150%' } : {}),
          color: variant === 'sidebar' ? '#6B1224' : 'rgba(0, 0, 0, 0.6)',
          textAlign: alignTo,
          display: 'block',
          fontWeight: variant === 'sidebar' ? 800 : 500,
          mb: 0.5,
          maxWidth: '21ch',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
          ...(variant === 'sidebar' ? { fontFamily: 'Noto Sans Hebrew' } : {})
        }}
      >
        {fullName}
      </Box>
      <Tooltip title={branchName}>
        <Box component="span" sx={branchNameSx}>
          {branchName}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ProfileSection;
